import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { apiPut } from "../../utils/api";
import { useLanguage } from "../../contexts/LanguageContext";
import { AdminField } from "./AdminFormDialog";
import { AdminPanel, AdminBtnPrimary } from "./AdminUI";
import { Textarea } from "../ui/input";
import { ImageUpload } from "../subcomponents/ImageUpload";

const THEME_KEY = "annual_theme";
const IMAGE_KEY = "annual_theme_image";
const DEFAULT_THEME = "L'intelligence artificielle au service de l'excellence académique";

type Props = {
  item: { theme?: string; imageUrl?: string } | null;
  onSaved: (item: { theme: string; imageUrl: string }) => void;
};

export const AdminSiteSettingsPanel: React.FC<Props> = ({ item, onSaved }) => {
  const { t } = useLanguage();
  const [theme, setTheme] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setTheme(item?.theme || DEFAULT_THEME);
    setImageUrl(item?.imageUrl || "");
  }, [item]);

  const handleSave = async () => {
    const nextTheme = theme.trim();
    if (!nextTheme) {
      setError(t.admin.annualThemeRequired);
      return;
    }
    setError("");
    setSaving(true);
    try {
      await Promise.all([
        apiPut(`/site-settings/${THEME_KEY}`, {
          value: nextTheme,
          label: t.admin.annualTheme,
          description: t.admin.annualThemeDesc,
        }),
        apiPut(`/site-settings/${IMAGE_KEY}`, {
          value: imageUrl.trim(),
          label: t.admin.annualThemeImage,
          description: t.admin.annualThemeImageHint,
        }),
      ]);
      const saved = { theme: nextTheme, imageUrl: imageUrl.trim() };
      onSaved(saved);
      toast.success(t.admin.annualThemeSaved);
    } catch (e: any) {
      toast.error(e?.messageText || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminPanel>
      <div style={{ padding: "24px 28px 28px" }}>
        <p style={{ color: "#666", fontSize: 13, marginTop: 0, marginBottom: 20 }}>
          {t.admin.annualThemeDesc}
        </p>

        <AdminField label={t.admin.annualThemeLabel} required error={error}>
          <Textarea
            rows={4}
            value={theme}
            onChange={(e) => setTheme(e.target.value)}
            placeholder={DEFAULT_THEME}
            data-testid="annual-theme-input"
          />
        </AdminField>

        <ImageUpload
          value={imageUrl}
          onChange={setImageUrl}
          folder="ulpgl/uploads"
          label={t.admin.annualThemeImage}
          testId="annual-theme-image"
        />
        <p style={{ color: "#888", fontSize: 12, marginTop: 8, marginBottom: 0 }}>
          {t.admin.annualThemeImageHint}
        </p>

        <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end" }}>
          <AdminBtnPrimary onClick={handleSave} disabled={saving} data-testid="annual-theme-save">
            {saving ? "..." : t.admin.save}
          </AdminBtnPrimary>
        </div>
      </div>
    </AdminPanel>
  );
};
